import { useEffect, useRef } from "react";

export const ONETRUST_COOKIE_UNDEFINED_ERROR_CODE =
  "ONETRUST_COOKIE_UNDEFINED";
export const ONETRUST_SDK_LOAD_FAILED_ERROR_CODE =
  "ONETRUST_SDK_LOAD_FAILED";

type OneTrustCookieMissingSurface = "MP" | "MOH" | "CP";
type OneTrustCookieMissingSeverity = "debug" | "info" | "warning" | "error";
type OneTrustErrorCode =
  | typeof ONETRUST_COOKIE_UNDEFINED_ERROR_CODE
  | typeof ONETRUST_SDK_LOAD_FAILED_ERROR_CODE;

export type OneTrustCookieMissingEventProperties = {
  error_code: OneTrustErrorCode;
  category: "third_party";
  surface: OneTrustCookieMissingSurface;
  operation: string;
  message: string;
  severity: OneTrustCookieMissingSeverity;
  is_recoverable: boolean;
  retry_count: number;
  latency_ms: number;
  merchant_id?: string;
};

type TrackCookieMissingEvent = (
  eventName: "Error Reported",
  properties: OneTrustCookieMissingEventProperties,
) => Promise<unknown> | void;

export type OneTrustCookieMissingReporterProps = {
  enabled: boolean;
  surface: OneTrustCookieMissingSurface;
  track: TrackCookieMissingEvent;
  merchantId?: string | null;
  operation?: string;
  message?: string;
  severity?: OneTrustCookieMissingSeverity;
  isRecoverable?: boolean;
  retryCount?: number;
  errorCode?: OneTrustErrorCode;
};

type CreateOneTrustCookieMissingEventPropertiesOptions = Omit<
  OneTrustCookieMissingReporterProps,
  "enabled" | "track"
> & {
  latencyMs?: number;
};

export function createOneTrustCookieMissingEventProperties({
  surface,
  merchantId,
  operation = "onetrust.cookie_check",
  message = "OptanonConsent cookie is undefined after page load",
  severity = "warning",
  isRecoverable = true,
  retryCount = 0,
  latencyMs,
  errorCode = ONETRUST_COOKIE_UNDEFINED_ERROR_CODE,
}: CreateOneTrustCookieMissingEventPropertiesOptions): OneTrustCookieMissingEventProperties {
  const resolvedLatencyMs =
    latencyMs ??
    (typeof performance === "undefined" ? 0 : Math.round(performance.now()));

  return {
    error_code: errorCode,
    category: "third_party",
    surface,
    operation,
    message,
    severity,
    is_recoverable: isRecoverable,
    retry_count: retryCount,
    latency_ms: resolvedLatencyMs,
    ...(merchantId ? { merchant_id: merchantId } : {}),
  };
}

export function OneTrustCookieMissingReporter({
  enabled,
  surface,
  track,
  merchantId,
  operation,
  message,
  severity,
  isRecoverable,
  retryCount,
  errorCode,
}: Readonly<OneTrustCookieMissingReporterProps>): null {
  const reported = useRef(false);

  useEffect(() => {
    const browserWindow = globalThis.window;
    if (!enabled || reported.current || browserWindow === undefined) return;

    const report = () => {
      if (reported.current) return;
      reported.current = true;

      const properties = createOneTrustCookieMissingEventProperties({
        surface,
        merchantId,
        operation,
        message,
        severity,
        isRecoverable,
        retryCount,
        errorCode,
      });

      void Promise.resolve(track("Error Reported", properties)).catch(() => {
        // Monitoring must never break the host application.
      });
    };

    if (document.readyState === "complete") {
      report();
      return;
    }

    browserWindow.addEventListener("load", report, { once: true });
    return () => browserWindow.removeEventListener("load", report);
  }, [
    enabled,
    isRecoverable,
    merchantId,
    message,
    operation,
    retryCount,
    errorCode,
    severity,
    surface,
    track,
  ]);

  return null;
}
